// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect, useRef } from 'react';
import providerAccessApi from '../api/providerAccessApi';
import type {
  ProviderAccessPageContext,
  ProviderSubmissionResult,
  ProviderFormSubmissionRequest,
  ProviderRejectionRequest,
  ProviderFeedbackRequest,
  ProviderAccessErrorResponse,
  ProviderPageState,
} from '../types/providerAccess';

interface UseProviderAccessResult {
  /** Page context from token validation */
  context: ProviderAccessPageContext | null;
  /** Current page state */
  pageState: ProviderPageState;
  /** Error details (when pageState === 'error') */
  error: ProviderAccessErrorResponse | null;
  /** Last submission result (when pageState === 'submitted') */
  submissionResult: ProviderSubmissionResult | null;
  /** Whether a submission is in progress */
  submitting: boolean;
  /** Upload progress percentage (0-100) */
  uploadProgress: number;
  /** Seconds since the page loaded (for honeypot timing) */
  formOpenDuration: number;

  /** Submit structured form data */
  submitForm: (data: ProviderFormSubmissionRequest) => Promise<boolean>;
  /** Upload a document file with optional wizard context */
  uploadDocument: (file: File, stepId?: string, fieldKey?: string) => Promise<boolean>;
  /** Reject the LOA */
  rejectLoa: (request: ProviderRejectionRequest) => Promise<boolean>;
  /** Submit optional feedback */
  submitFeedback: (request: ProviderFeedbackRequest) => Promise<boolean>;
  /** Phase 9: accept the Change of Agency (only valid when context.documentType === 'COA') */
  acceptCoa: (formData?: Record<string, unknown>) => Promise<boolean>;
  /** Phase 9: reject the Change of Agency (only valid when context.documentType === 'COA') */
  rejectCoa: (request: ProviderRejectionRequest) => Promise<boolean>;
  /** New Business: issue a policy against the application (only valid when documentType === 'NEW_BUSINESS') */
  issueNewBusiness: (policyNumber: string) => Promise<boolean>;
  /** New Business: reject the application (only valid when documentType === 'NEW_BUSINESS') */
  rejectNewBusiness: (request: ProviderRejectionRequest) => Promise<boolean>;
  /** Transition to feedback page state */
  goToFeedback: () => void;
  /** Go back to the active form (after submission, if more submissions allowed) */
  resetToActive: () => void;
  /** Transition to submitted/confirmation page state */
  goToSubmitted: () => void;
}

export function useProviderAccess(token: string): UseProviderAccessResult {
  const [context, setContext] = useState<ProviderAccessPageContext | null>(null);
  const [pageState, setPageState] = useState<ProviderPageState>('loading');
  const [error, setError] = useState<ProviderAccessErrorResponse | null>(null);
  const [submissionResult, setSubmissionResult] = useState<ProviderSubmissionResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formOpenDuration, setFormOpenDuration] = useState(0);
  const pageLoadTime = useRef(Date.now());
  const hasSubmitted = useRef(false);

  // Track time since page load for honeypot timing
  useEffect(() => {
    const interval = setInterval(() => {
      setFormOpenDuration(Math.floor((Date.now() - pageLoadTime.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Validate token on mount
  useEffect(() => {
    const validate = async () => {
      try {
        const ctx = await providerAccessApi.validateToken(token);
        setContext(ctx);
        if (ctx.loaRejected) {
          setPageState('rejected');
        } else {
          setPageState('active');
        }
      } catch (err) {
        setError(providerAccessApi.extractError(err));
        setPageState('error');
      }
    };
    validate();
  }, [token]);

  const updateContextAfterSubmission = useCallback((result: ProviderSubmissionResult) => {
    setContext((prev) =>
      prev
        ? {
            ...prev,
            remainingSubmissions: result.remainingSubmissions,
            submissionAllowed: result.remainingSubmissions > 0,
            loaRejected: result.submissionType === 'REJECTION' ? true : prev.loaRejected,
          }
        : prev,
    );
  }, []);

  const submitForm = useCallback(
    async (data: ProviderFormSubmissionRequest): Promise<boolean> => {
      if (hasSubmitted.current) return false;
      setSubmitting(true);
      try {
        const result = await providerAccessApi.submitForm(token, {
          ...data,
          formOpenDurationSeconds: formOpenDuration,
        });
        hasSubmitted.current = true;
        setSubmissionResult(result);
        updateContextAfterSubmission(result);
        // Wizard flow: transition directly to submitted success page
        setPageState('submitted');
        return true;
      } catch (err) {
        setError(providerAccessApi.extractError(err));
        setPageState('error');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [token, formOpenDuration, updateContextAfterSubmission],
  );

  const uploadDocument = useCallback(
    async (file: File, stepId?: string, fieldKey?: string): Promise<boolean> => {
      setSubmitting(true);
      setUploadProgress(0);
      try {
        const result = await providerAccessApi.uploadDocument(
          token,
          file,
          stepId,
          fieldKey,
          setUploadProgress,
        );
        setSubmissionResult(result);
        updateContextAfterSubmission(result);
        return true;
      } catch (err) {
        setError(providerAccessApi.extractError(err));
        return false;
      } finally {
        setSubmitting(false);
        setUploadProgress(0);
      }
    },
    [token, updateContextAfterSubmission],
  );

  const rejectLoa = useCallback(
    async (request: ProviderRejectionRequest): Promise<boolean> => {
      if (hasSubmitted.current) return false;
      setSubmitting(true);
      try {
        const result = await providerAccessApi.rejectLoa(token, request);
        hasSubmitted.current = true;
        setSubmissionResult(result);
        updateContextAfterSubmission(result);
        setPageState('rejected');
        return true;
      } catch (err) {
        setError(providerAccessApi.extractError(err));
        setPageState('error');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [token, updateContextAfterSubmission],
  );

  const submitFeedback = useCallback(
    async (request: ProviderFeedbackRequest): Promise<boolean> => {
      setSubmitting(true);
      try {
        await providerAccessApi.submitFeedback(token, request);
        setPageState('submitted');
        return true;
      } catch (err) {
        // Feedback is optional; on 409 (already submitted), just go to submitted page
        const parsedError = providerAccessApi.extractError(err);
        if (parsedError.code === 'FEEDBACK_ALREADY_SUBMITTED') {
          setPageState('submitted');
          return true;
        }
        // For other errors, still go to submitted (feedback is non-critical)
        setPageState('submitted');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [token],
  );

  // ============================================================ //
  // Phase 9: Change of Agency accept/reject
  // ============================================================ //

  const acceptCoa = useCallback(
    async (formData: Record<string, unknown> = {}): Promise<boolean> => {
      if (hasSubmitted.current) return false;
      setSubmitting(true);
      try {
        const result = await providerAccessApi.acceptCoa(token, formData);
        hasSubmitted.current = true;
        setSubmissionResult(result);
        updateContextAfterSubmission(result);
        setPageState('submitted');
        return true;
      } catch (err) {
        setError(providerAccessApi.extractError(err));
        setPageState('error');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [token, updateContextAfterSubmission],
  );

  const rejectCoa = useCallback(
    async (request: ProviderRejectionRequest): Promise<boolean> => {
      if (hasSubmitted.current) return false;
      setSubmitting(true);
      try {
        const result = await providerAccessApi.rejectCoa(token, request);
        hasSubmitted.current = true;
        setSubmissionResult(result);
        updateContextAfterSubmission(result);
        // CoA rejection lands on the same "submitted" success screen as
        // accept — the rejection reason is shown on the result card so
        // the provider knows what they sent. Mirrors the LOA "rejected"
        // page state but uses 'submitted' so the existing
        // ProviderAccessSubmitted component can render the result.
        setPageState('submitted');
        return true;
      } catch (err) {
        setError(providerAccessApi.extractError(err));
        setPageState('error');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [token, updateContextAfterSubmission],
  );

  const issueNewBusiness = useCallback(
    async (policyNumber: string): Promise<boolean> => {
      if (hasSubmitted.current) return false;
      setSubmitting(true);
      try {
        const result = await providerAccessApi.issueNewBusiness(token, policyNumber);
        hasSubmitted.current = true;
        setSubmissionResult(result);
        updateContextAfterSubmission(result);
        setPageState('submitted');
        return true;
      } catch (err) {
        setError(providerAccessApi.extractError(err));
        setPageState('error');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [token, updateContextAfterSubmission],
  );

  const rejectNewBusiness = useCallback(
    async (request: ProviderRejectionRequest): Promise<boolean> => {
      if (hasSubmitted.current) return false;
      setSubmitting(true);
      try {
        const result = await providerAccessApi.rejectNewBusiness(token, request);
        hasSubmitted.current = true;
        setSubmissionResult(result);
        updateContextAfterSubmission(result);
        setPageState('submitted');
        return true;
      } catch (err) {
        setError(providerAccessApi.extractError(err));
        setPageState('error');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [token, updateContextAfterSubmission],
  );

  const goToFeedback = useCallback(() => {
    setPageState('feedback');
  }, []);

  const goToSubmitted = useCallback(() => {
    setPageState('submitted');
  }, []);

  const resetToActive = useCallback(() => {
    setSubmissionResult(null);
    setError(null);
    setPageState('active');
    pageLoadTime.current = Date.now();
  }, []);

  return {
    context,
    pageState,
    error,
    submissionResult,
    submitting,
    uploadProgress,
    formOpenDuration,
    submitForm,
    uploadDocument,
    rejectLoa,
    submitFeedback,
    acceptCoa,
    rejectCoa,
    issueNewBusiness,
    rejectNewBusiness,
    goToFeedback,
    resetToActive,
    goToSubmitted,
  };
}
